/**
 * Task Structure Section
 *
 * Consolidated section for:
 * - Standalone/Parent task selection
 * - On Child Failure setting (inline radios)
 * - Child task list with add button
 */

import { UseFormReturn } from 'react-hook-form';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '../../ui/form';
import { RadioGroup, RadioGroupItem } from '../../ui/radio-group';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Loader2, Plus, Users } from 'lucide-react';
import { ChildTaskList } from './ChildTaskList';
import type { ScheduledTask } from '../../../lib/api';

interface TaskStructureSectionProps {
  form: UseFormReturn<any>;
  taskStructure: 'standalone' | 'parent';
  mode: 'create' | 'edit';
  taskId?: string;
  childTasks: ScheduledTask[];
  isLoadingChildren: boolean;
  parentWithoutChildrenWarning: boolean;
  onAddChild: () => void;
  onMoveChild: (childId: string, direction: 'up' | 'down') => void;
  onRemoveChild: (childId: string) => void;
  onEditChild: (childId: string) => void;
}

export function TaskStructureSection({
  form,
  taskStructure,
  mode,
  taskId,
  childTasks,
  isLoadingChildren,
  parentWithoutChildrenWarning,
  onAddChild,
  onMoveChild,
  onRemoveChild,
  onEditChild,
}: TaskStructureSectionProps) {
  const hasChildren = childTasks.length > 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Task Structure</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {taskStructure === 'parent' && parentWithoutChildrenWarning && mode === 'edit' && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Add at least one child task before saving.
          </div>
        )}

        {/* Standalone/Parent selection - inline radios */}
        <FormField
          control={form.control}
          name="taskStructure"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  value={field.value}
                  className="flex gap-6"
                  disabled={mode === 'edit' && hasChildren}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="standalone" id="standalone" />
                    <Label htmlFor="standalone" className="font-normal cursor-pointer">
                      Standalone
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="parent" id="parent" />
                    <Label htmlFor="parent" className="font-normal cursor-pointer flex items-center gap-1.5">
                      <Users className="h-3.5 w-3.5" />
                      Parent with Children
                    </Label>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Parent task options */}
        {taskStructure === 'parent' && (
          <>
            {/* On Child Failure - inline radios */}
            <div className="pt-3 border-t">
              <FormField
                control={form.control}
                name="onChildFailure"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-3">
                      <Label className="text-sm shrink-0">On Child Failure:</Label>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          value={field.value}
                          className="flex gap-4"
                        >
                          <div className="flex items-center space-x-1.5">
                            <RadioGroupItem value="stop" id="onFailure-stop" />
                            <Label htmlFor="onFailure-stop" className="font-normal cursor-pointer text-sm">
                              Stop
                            </Label>
                          </div>
                          <div className="flex items-center space-x-1.5">
                            <RadioGroupItem value="skip" id="onFailure-skip" />
                            <Label htmlFor="onFailure-skip" className="font-normal cursor-pointer text-sm">
                              Skip
                            </Label>
                          </div>
                          <div className="flex items-center space-x-1.5">
                            <RadioGroupItem value="retry" id="onFailure-retry" />
                            <Label htmlFor="onFailure-retry" className="font-normal cursor-pointer text-sm">
                              Retry
                            </Label>
                          </div>
                        </RadioGroup>
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Child Tasks - only in edit mode with taskId */}
            {mode === 'edit' && taskId && (
              <div className="pt-3 border-t">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm">Child Tasks</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={onAddChild}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add
                  </Button>
                </div>

                {isLoadingChildren ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <ChildTaskList
                    childTasks={childTasks}
                    parentTaskId={taskId}
                    onMoveChild={onMoveChild}
                    onRemoveChild={onRemoveChild}
                    onEditChild={onEditChild}
                  />
                )}
              </div>
            )}

            {/* Message for create mode */}
            {mode === 'create' && (
              <div className="pt-3 border-t">
                <p className="text-xs text-muted-foreground">
                  Click "Create Task" to save, then you'll be redirected to add child tasks.
                </p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
